import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import type { TaskTypeView, TaskTypeViewRegion } from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  moveViewRegion,
  regionAddActions,
  removeViewRegion,
  setViewRegionColumn,
  setViewRegionTitle,
  supplierLinesBlockedByChildren,
  type RegionAddAction,
  type RegionAddKind,
  type ViewSchemaField,
} from './taskTypeViewModel';
import { TaskTypeViewPlacementRow } from './TaskTypeViewPlacementRow';

export function TaskTypeViewRegionCard({
  view,
  region,
  index,
  count,
  fields,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  region: TaskTypeViewRegion;
  index: number;
  count: number;
  fields: readonly ViewSchemaField[];
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  function commit(next: TaskTypeView) {
    if (next !== view) onCommit(next);
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={region.title ?? ''}
          disabled={disabled}
          className="max-w-sm"
          placeholder={
            region.column === 'aside'
              ? gt('Tab title', {
                  context:
                    'placeholder for a task page side section title, shown as a tab',
                })
              : gt('Section title', {
                  context: 'placeholder for a task page section title',
                })
          }
          aria-label={gt('Section title', {
            context: 'task page builder section title',
          })}
          onChange={(event) =>
            commit(setViewRegionTitle(view, region.id, event.target.value))
          }
        />
        <ToggleGroup
          type="single"
          value={region.column}
          disabled={disabled}
          aria-label={gt('Section column', {
            context:
              'task page builder, whether a section renders in the main column or beside it',
          })}
          onValueChange={(value) => {
            if (value !== 'main' && value !== 'aside') return;
            commit(setViewRegionColumn(view, region.id, value));
          }}
        >
          <ToggleGroupItem value="main">
            <T context="task page builder, section renders in the main column">
              Main
            </T>
          </ToggleGroupItem>
          <ToggleGroupItem value="aside">
            <T context="task page builder, section renders as a tab in the side panel">
              Side
            </T>
          </ToggleGroupItem>
        </ToggleGroup>
        {region.column === 'aside' ? (
          <span className="text-xs text-muted-foreground">
            <T context="task page builder, hint for a side section">
              A tab in the side panel, before Task Activity and Task Notes.
              Section order is tab order.
            </T>
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index === 0}
            aria-label={gt('Move section up', {
              context: 'task page builder, move a section up',
            })}
            onClick={() => commit(moveViewRegion(view, region.id, -1))}
          >
            <ArrowUpIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index >= count - 1}
            aria-label={gt('Move section down', {
              context: 'task page builder, move a section down',
            })}
            onClick={() => commit(moveViewRegion(view, region.id, 1))}
          >
            <ArrowDownIcon />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={gt('Remove section', {
              context: 'task page builder, remove a section',
            })}
            onClick={() => commit(removeViewRegion(view, region.id))}
          >
            <TrashIcon />
          </Button>
        </div>
      </div>

      {region.placements.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T context="task page builder empty section">
            Nothing in this section yet.
          </T>
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {region.placements.map((placement, placementIndex) => (
            <TaskTypeViewPlacementRow
              key={placement.id}
              view={view}
              regionId={region.id}
              placement={placement}
              index={placementIndex}
              count={region.placements.length}
              fields={fields}
              fieldsReady={fieldsReady}
              disabled={disabled}
              onCommit={onCommit}
            />
          ))}
        </div>
      )}

      <RegionAddButtons
        view={view}
        regionId={region.id}
        fields={fields}
        fieldsReady={fieldsReady}
        disabled={disabled}
        onCommit={onCommit}
      />
    </div>
  );
}

function RegionAddButtons({
  view,
  regionId,
  fields,
  fieldsReady,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  fields: readonly ViewSchemaField[];
  fieldsReady: boolean;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const actions = regionAddActions(view, regionId, fields, fieldsReady);
  const remapBlocked = fieldsReady && supplierLinesBlockedByChildren(fields);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => (
          <RegionAddButton
            key={action.kind}
            action={action}
            disabled={disabled}
            onCommit={onCommit}
          />
        ))}
      </div>
      {remapBlocked ? (
        <p className="text-xs text-muted-foreground">
          <T context="task page builder, why Add Supplier Lines is disabled">
            Supplier Lines needs a json or array field with no child fields, so
            a saved remap keeps the match row's identifiers and product. Declare
            the match rows as a childless field and map extracted columns on the
            placement instead.
          </T>
        </p>
      ) : null}
    </div>
  );
}

function RegionAddButton({
  action,
  disabled,
  onCommit,
}: {
  action: RegionAddAction;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A span keeps the tooltip reachable when the button is disabled. */}
        <span className="inline-flex">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={addButtonDisabled(disabled, action.enabled)}
            onClick={() => commitAdd(action, onCommit)}
          >
            <PlusIcon />
            <RegionAddLabel kind={action.kind} />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">
        {ADD_HINTS[action.kind]}
      </TooltipContent>
    </Tooltip>
  );
}

function addButtonDisabled(
  disabled: boolean | undefined,
  enabled: boolean,
): boolean {
  return Boolean(disabled) || !enabled;
}

function commitAdd(
  action: RegionAddAction,
  onCommit: (next: TaskTypeView) => void,
): void {
  if (action.next) onCommit(action.next);
}

function RegionAddLabel({ kind }: { kind: RegionAddKind }) {
  return ADD_LABELS[kind];
}

const ADD_LABELS: Record<RegionAddKind, ReactNode> = {
  field: <T context="task page builder, place a schema field">Add Field</T>,
  match: (
    <T context="task page builder, place a supplier or product match">
      Add Match
    </T>
  ),
  lineList: (
    <T context="task page builder, place a table of line items">
      Add Line List
    </T>
  ),
  supplierLines: (
    <T context="task page builder, place supplier document line remapping">
      Add Supplier Lines
    </T>
  ),
  email: <T context="task page builder, place an email viewer">Add Email</T>,
  document: (
    <T context="task page builder, place a document viewer">Add Document</T>
  ),
  apInvoice: (
    <T context="task page builder, place the AP invoice detail panel">
      Add AP Invoice
    </T>
  ),
  cashReceipt: (
    <T context="task page builder, place the cash receipt detail">
      Add Cash Receipt
    </T>
  ),
};

/** What each add button places. Shown on hover, including when disabled. */
const ADD_HINTS: Record<RegionAddKind, ReactNode> = {
  field: (
    <T context="task page builder tooltip, Add Field">
      One field from the input schema, read-only or editable.
    </T>
  ),
  match: (
    <T context="task page builder tooltip, Add Match">
      A supplier or product typeahead that stores the chosen id in a string
      field. Not for extracted document lines.
    </T>
  ),
  lineList: (
    <T context="task page builder tooltip, Add Line List">
      A table of rows from an array field whose children are declared on the
      schema. Each column is one of those child fields.
    </T>
  ),
  supplierLines: (
    <T context="task page builder tooltip, Add Supplier Lines">
      Rows from Match Supplier Document Lines: a match status per line that
      opens the product picker, editable extracted columns, and the matched
      product. Needs a json or array field with no children.
    </T>
  ),
  email: (
    <T context="task page builder tooltip, Add Email">
      The inbound email viewer, from the task anchor or an email field.
    </T>
  ),
  document: (
    <T context="task page builder tooltip, Add Document">
      The document viewer, from the task anchor or a document field. Put it in a
      Side section to show it beside the fields.
    </T>
  ),
  apInvoice: (
    <T context="task page builder tooltip, Add AP Invoice">
      The AP invoice detail, the same panel as the invoice slide-in, from the
      task anchor or an invoice field.
    </T>
  ),
  cashReceipt: (
    <T context="task page builder tooltip, Add Cash Receipt">
      The cash receipt detail, the same view as the cash receipts page without
      its action buttons, from the task anchor or a cash receipt field.
    </T>
  ),
};
